import { useEffect, useRef } from 'react';
import type { ChartItem } from '@/types/api';

interface MonitorChartProps {
  items: ChartItem[];
  type: 'ndvi' | 'tof';
}

declare global {
  interface Window {
    Chart: any;
  }
}

function formatLabel(regdate: string): string {
  // regdate: "2026-04-18 17:26:30" → "04-18 17:26"
  if (regdate && regdate.length >= 16) {
    return regdate.substring(5, 16);
  }
  return regdate;
}

export default function MonitorChart({ items, type }: MonitorChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (!canvasRef.current || items.length === 0) return;

    const labels = items.map(r => formatLabel(r.regdate));

    let data: number[];
    let color: string;
    let label: string;

    if (type === 'ndvi') {
      data = items.map(r => Number(r.ndvi_avg));
      color = '#27ae60';
      label = 'NDVI 평균';
    } else {
      // tof: show both tof_center_mm and rice_height_mm
      data = items.map(r => Number(r.tof_center_mm));
      color = '#2E86C1';
      label = 'ToF 중앙 거리 (mm)';
    }

    const buildChart = () => {
      if (!window.Chart || !canvasRef.current) return;
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const datasets: any[] = [
        {
          label,
          data,
          borderColor: color,
          backgroundColor: color + '22',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: color,
          tension: 0.3,
          fill: true,
        },
      ];

      // For tof type, also show rice_height_mm
      if (type === 'tof') {
        datasets.push({
          label: '벼 높이 (mm)',
          data: items.map(r => Number(r.rice_height_mm)),
          borderColor: '#27ae60',
          backgroundColor: '#27ae6022',
          borderWidth: 2,
          pointRadius: 3,
          pointBackgroundColor: '#27ae60',
          tension: 0.3,
          fill: false,
        });
      }

      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'line',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: 'top' },
            tooltip: { mode: 'index', intersect: false },
          },
          scales: {
            x: {
              ticks: { maxRotation: 45, font: { size: 10 } },
              grid: { color: '#f0f0f0' },
            },
            y:
              type === 'ndvi'
                ? { min: -0.5, max: 1.0, grid: { color: '#f0f0f0' } }
                : { grid: { color: '#f0f0f0' } },
          },
        },
      });
    };

    if (window.Chart) {
      buildChart();
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.onload = buildChart;
      document.head.appendChild(script);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [items, type]);

  return (
    <div style={{ height: 220, position: 'relative' }}>
      <canvas ref={canvasRef}></canvas>
    </div>
  );
}
